/**
 * inferThreadLabel
 *
 * One cheap LLM call that produces a human-readable short label and a
 * 1–2 sentence topic summary for a chat thread, based on its current
 * message history. Used by the ThreadSwitcherBar to:
 *
 *   • Replace the default "New thread" / "Nuevo hilo" chip name with a
 *     concise, representative title (≤5 words, Title Case) as soon as the
 *     conversation has any signal.
 *   • Power the chip's hover/focus tooltip with `topicSummary`.
 *
 * Implementation notes
 * ────────────────────
 * • Reuses the existing Anthropic proxy URL helper that legalDocCreator and
 *   the rest of the global-tool services already use — no new SDK, no new
 *   network surface to authenticate.
 * • Streaming is disabled here (single short response, ~50 tokens of JSON).
 *   Total latency ~1–2s, runs fire-and-forget so it never blocks the
 *   reply path.
 * • Honours an optional AbortSignal so the caller can cancel an in-flight
 *   inference when a fresher user message arrives — prevents fan-out and
 *   keeps the chip label tracking the *latest* user intent.
 * • Returns an empty `{ shortName: '', topicSummary: '' }` on any failure
 *   (timeout, abort, parse error, network) so callers can simply skip
 *   updating the thread record on degenerate output.
 */

import type { ChatMessage } from '../../components/GlobalChatSystem/types';
import { htmlToPlainText } from '../../components/GlobalChatSystem/utils';
import { THREAD_LABEL_PRIMARY } from '../../config/modelTiers.generated';
import type { UiLanguage } from './uiLanguage';

export interface InferredThreadLabel {
  /** ≤5-word Title Case label suitable for the chip name. May be empty. */
  shortName: string;
  /** 1–2 sentence summary suitable for hover/focus tooltip. May be empty. */
  topicSummary: string;
}

interface InferOptions {
  signal?: AbortSignal;
  /**
   * Override the proxy URL (mostly for tests). When omitted we resolve the
   * same URL legalDocCreator does so behaviour is consistent.
   */
  proxyUrl?: string;
  /** Override the model id. Defaults to the SSOT thread-label route primary. */
  model?: string;
}

// ────────────────────────────────────────────────────────────────────────────
// URL resolution — mirrors legalDocCreator.ts so we don't introduce drift.
// ────────────────────────────────────────────────────────────────────────────

function resolveAnthropicProxyUrl(): string {
  const override = (process.env.REACT_APP_ANTHROPIC_PROXY_URL || '').trim();
  if (override) return override;
  const projectId =
    process.env.REACT_APP_FIREBASE_PROJECT_ID || 'tensile-tenure-453409-k3';
  return `https://us-central1-${projectId}.cloudfunctions.net/anthropicProxy`;
}

/** Maximum chars per message we feed the labeller. Keeps prompt cheap. */
const MAX_PER_MESSAGE_CHARS = 1500;
/** Maximum messages from the tail we include in the prompt. */
const MAX_MESSAGES = 12;
/** Hard upper bound on total prompt body size — protects against runaway threads. */
const MAX_TOTAL_PROMPT_CHARS = 12_000;

/**
 * Flatten a chat message into a single line of plain text suitable for
 * the labeller. Strips HTML, collapses whitespace, hard-truncates per
 * message so a runaway streaming response can't dominate the prompt.
 */
function messageToLine(m: ChatMessage): string {
  const raw = (m.text && m.text.trim())
    || (m.html ? htmlToPlainText(m.html).trim() : '')
    || '';
  if (!raw) return '';
  const oneLine = raw.replace(/\s+/g, ' ').trim();
  const trimmed = oneLine.length > MAX_PER_MESSAGE_CHARS
    ? oneLine.slice(0, MAX_PER_MESSAGE_CHARS) + '…'
    : oneLine;
  const role = m.isUser ? 'User' : 'Assistant';
  return `${role}: ${trimmed}`;
}

/** Build the user prompt body from the message list. */
function buildPromptBody(messages: ChatMessage[]): string {
  // Take the tail — most recent messages best represent current topic.
  const tail = messages.slice(-MAX_MESSAGES);
  const lines: string[] = [];
  let total = 0;
  for (const m of tail) {
    const line = messageToLine(m);
    if (!line) continue;
    if (total + line.length > MAX_TOTAL_PROMPT_CHARS) break;
    lines.push(line);
    total += line.length + 1;
  }
  return lines.join('\n');
}

/**
 * Pull the first balanced JSON object out of a model response. Tolerates
 * leading commentary or surrounding markdown fences.
 */
function extractJson(text: string): Record<string, unknown> | null {
  if (!text) return null;
  const match = text.match(/\{[\s\S]*\}/);
  if (!match) return null;
  try {
    return JSON.parse(match[0]) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Trim a string to ≤ `maxWords` words. Words are whitespace-separated.
 * Preserves Title Case; drops trailing punctuation that would look weird
 * on a chip.
 */
function clampWords(s: string, maxWords: number): string {
  const cleaned = s.replace(/\s+/g, ' ').trim();
  if (!cleaned) return '';
  const parts = cleaned.split(' ');
  const limited = parts.slice(0, maxWords).join(' ');
  return limited.replace(/[.,;:!?\u2026\s]+$/u, '').trim();
}

/**
 * True when a model ref is Anthropic-family (tolerates an `openrouter/` prefix and the
 * `~` always-latest alias marker). Mirrors fleetModelProxy's isAnthropicModelRef — the
 * gate for Anthropic-only request-body rules (no sampling params, thinking-aware
 * max_tokens budget).
 */
function isAnthropicModelRef(modelId: string): boolean {
  let id = (modelId || '').trim().toLowerCase();
  if (id.startsWith('openrouter/')) id = id.slice('openrouter/'.length);
  if (id.startsWith('~')) id = id.slice(1);
  return id.startsWith('anthropic/');
}

/**
 * True when a model ref is OpenAI codex-family (tolerates an `openrouter/` prefix).
 * Mirrors fleetModelProxy's isOpenAiCodexModelRef — codex rejects temperature and its
 * unremovable reasoning shares the max_tokens completion budget (2026-07-05 Tier-2 swap:
 * gpt-5.3-codex is the thread-label route's primary).
 */
function isOpenAiCodexModelRef(modelId: string): boolean {
  let id = (modelId || '').trim().toLowerCase();
  if (id.startsWith('openrouter/')) id = id.slice('openrouter/'.length);
  return /^openai\/gpt-[\d.]+-codex($|[^a-z])/.test(id);
}

/**
 * Run a single LLM call to infer a chip label and topic summary.
 *
 * Fails closed: any error (network, abort, parse) returns empty strings so
 * the caller skips updating the thread record. The caller MUST treat this
 * as fire-and-forget — no UI affordance should block on the promise.
 */
export async function inferThreadLabel(
  messages: ChatMessage[],
  /**
   * The user's UI language — NOT the language detected from the
   * thread's content. Bug G: titles must always render in the UI
   * language so the chip list stays consistent with the rest of the
   * interface. Resolve via `getUserUiLanguage()` before calling.
   */
  language: UiLanguage,
  options: InferOptions = {}
): Promise<InferredThreadLabel> {
  const empty: InferredThreadLabel = { shortName: '', topicSummary: '' };

  // Need at least one user message to have anything meaningful to label.
  if (!messages.some(m => m.isUser)) return empty;

  const proxyUrl = options.proxyUrl || resolveAnthropicProxyUrl();
  // 2026-05-25 (Miles directive, Option 2): thread labeling rides Tier 2 Mid-Tier.
  // The id comes from the generated SSOT constant (taskRouting['thread-label']) —
  // the previous hand-pinned 'google/gemini-3.5-flash' literal here fossilized when
  // the Tier-2 primary moved, and was the model behind the 2026-07-02 request storm.
  const model = options.model || THREAD_LABEL_PRIMARY;

  const promptBody = buildPromptBody(messages);
  if (!promptBody.trim()) return empty;

  // Bug G (Miles 2026-05-11, thread `C0AM8U804KZ/1778281880.499349`):
  // chip labels MUST render in the user's UI language regardless of
  // the conversation-content language. The prompt below pins output
  // to `language` (the UI language passed in) and explicitly tells the
  // labeller to translate when the source messages are in a different
  // language. Caller is now responsible for resolving the UI language
  // through `getUserUiLanguage()` (see `services/threadMetadata/uiLanguage.ts`).
  const langInstruction = language === 'es'
    ? [
        'El idioma de la INTERFAZ del usuario es español.',
        'Responde SIEMPRE con etiquetas en español, aunque los mensajes del hilo estén escritos total o parcialmente en otro idioma (por ejemplo, inglés).',
        'Si los mensajes están en otro idioma, traduce el tema al español antes de generar shortName y topicSummary.',
      ].join(' ')
    : [
        "The user's INTERFACE language is English.",
        'ALWAYS respond with labels in English, even if the thread messages are written wholly or partly in another language (for example Spanish).',
        'If the messages are in another language, translate the topic into English before producing shortName and topicSummary.',
      ].join(' ');

  const systemPrompt = [
    "You label short chat threads.",
    "",
    "Return ONLY a single JSON object with exactly two fields:",
    "  - shortName: a label of AT MOST 5 words, in Title Case, that names what the thread is about. No quotes, no trailing punctuation.",
    "  - topicSummary: a 1–2 sentence summary describing what the user is asking about and what is happening in the thread.",
    "",
    langInstruction,
    "",
    // Low-signal default is the SENTENCE-CASE client default title. The old
    // Title-Case variants ("New Thread"/"Nuevo Hilo") were absent from
    // DEFAULT_TITLE_LITERALS, so labeler-defaulted empty threads stuck with a
    // "real"-looking title and evaded default-title reuse/cleanup logic (the
    // five-chip accumulation, 2026-07-12). The write site also normalizes.
    "If the thread does not yet have enough signal, return shortName as the localized default (English: \"New thread\"; Spanish: \"Nuevo hilo\") and topicSummary as a one-sentence neutral description.",
    "",
    "Output JSON ONLY. No commentary, no markdown fences.",
  ].join('\n');

  // 2026-08-20 decision doctrine — server-resolved decision transport FIRST: the client
  // sends only the route label; the SERVER resolves model/ladder/effort from the SSOT
  // (rollout-staged there), so the family-branch body below survives only as the
  // deploy-skew fallback for one release. The 'low' effort literal in that fallback is
  // dead code on today's grok primary and dies with the fallback.
  try {
    const { requestDecision } = await import('../decisionClient');
    const decided = await requestDecision({
      route: 'thread-label',
      system: systemPrompt,
      messages: [{ role: 'user', content: promptBody }],
      maxTokens: 1024,
      proxyUrl,
      signal: options.signal,
    });
    if (decided.text) {
      const parsedDecision = extractJson(decided.text);
      if (parsedDecision) {
        return {
          shortName: clampWords(typeof parsedDecision.shortName === 'string' ? parsedDecision.shortName : '', 5),
          topicSummary: (typeof parsedDecision.topicSummary === 'string' ? parsedDecision.topicSummary : '')
            .replace(/\s+/g, ' ')
            .trim(),
        };
      }
    }
  } catch {
    /* deploy-skew fallback: the legacy client transport below */
  }

  let response: Response;
  try {
    response = await fetch(proxyUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // Lets the proxy identify (and rate-shape) labeler traffic without
        // sniffing the prompt. Legacy bundles that predate this header get a
        // much stricter server-side limit.
        'X-Prest0n-Task': 'thread-label',
      },
      body: JSON.stringify({
        model,
        // Family-conditional params (tier2-compat, same rule as bce0f49; codex branch
        // added with the 2026-07-05 Tier-2 primary swap): Anthropic Sonnet-5-era models
        // REJECT non-default sampling params (temperature => HTTP 400) and share
        // max_tokens with adaptive thinking, so they get no temperature and a
        // 1024-token budget (thinking + the ~50-token JSON). Codex-family models reject
        // temperature outright and their UNREMOVABLE reasoning shares the completion
        // budget — no temperature, effort pinned low (a chip label needs no deep
        // reasoning), same 1024-token budget so reasoning can't truncate the JSON.
        // Non-Anthropic/non-codex rungs keep the tuned values:
        // 300, not 150: models that spend thinking tokens by default must
        // still have room to emit the ~50-token JSON — a truncated response
        // parses as empty and used to hold the caller's retry gate open.
        ...(isAnthropicModelRef(model)
          ? { max_tokens: 1024 }
          : isOpenAiCodexModelRef(model)
            ? { max_tokens: 1024, reasoning: { effort: 'low' } }
            : { max_tokens: 300, temperature: 0.2 }),
        // Disable streaming — short response, simpler error handling.
        stream: false,
        system: systemPrompt,
        messages: [{ role: 'user', content: promptBody }],
      }),
      signal: options.signal,
    });
  } catch {
    return empty;
  }

  if (!response.ok) return empty;

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return empty;
  }

  // Anthropic-style non-streaming response: { content: [{ type: 'text', text: '...' }] }
  // OpenAI-compatible response: { choices: [{ message: { content: '...' } }] }
  let text = '';
  if (body && typeof body === 'object') {
    const obj = body as Record<string, unknown>;
    const content = obj.content;
    if (Array.isArray(content)) {
      for (const part of content) {
        if (part && typeof part === 'object'
            && (part as Record<string, unknown>).type === 'text'
            && typeof (part as Record<string, unknown>).text === 'string') {
          text += (part as Record<string, unknown>).text as string;
        }
      }
    }
    if (!text) {
      const choices = obj.choices;
      if (Array.isArray(choices) && choices.length > 0) {
        const first = choices[0] as Record<string, unknown>;
        const msg = first.message as Record<string, unknown> | undefined;
        if (msg && typeof msg.content === 'string') text = msg.content;
      }
    }
    // Some proxies just return { text: '...' } directly.
    if (!text && typeof obj.text === 'string') text = obj.text;
  }

  if (!text) return empty;

  const parsed = extractJson(text);
  if (!parsed) return empty;

  const rawShort = typeof parsed.shortName === 'string' ? parsed.shortName : '';
  const rawSummary = typeof parsed.topicSummary === 'string' ? parsed.topicSummary : '';

  const shortName = clampWords(rawShort, 5);
  const topicSummary = rawSummary.replace(/\s+/g, ' ').trim();

  return { shortName, topicSummary };
}

export default inferThreadLabel;
